/** POD OAuth 同意授权页（浏览器） */
export const IPE_POD_OAUTH_AUTHORIZE_PATH = '/ipe/oauth/pod/authorize';

/** IPE 登录页（推荐入口） */
export const IPE_LOGIN_PATH = '/ipe/login';

/** 兼容旧书签 / 同事习惯的路径，与 /ipe/login 为同一登录页，不做 302 跳转 */
export const LEGACY_LOGIN_PATH = '/login';

export const IPE_LOGIN_PATHS = [IPE_LOGIN_PATH, LEGACY_LOGIN_PATH];

/** IPE 工作台（原 /ipe） */
export const IPE_WORKBENCH_PATH = '/ipe';

/** 新版商品套图设计器（对标 vue-fabric-editor） */
export const IPE_IMAGE_DESIGNER_PATH = `${IPE_WORKBENCH_PATH}/image-designer`;

/** 旧套图编辑器路径（S6 重定向至 image-designer） */
export const IPE_IMAGE_TEMPLATE_EDITOR_PATH = `${IPE_WORKBENCH_PATH}/image-template-editor`;

/** @param {string} [templateId] */
export function buildImageDesignerPath(templateId) {
  if (!templateId) return IPE_IMAGE_DESIGNER_PATH;
  return `${IPE_IMAGE_DESIGNER_PATH}/${encodeURIComponent(templateId)}`;
}

/** POD 套图二次编辑（ipelayer 微调，旧入口） */
export function buildPodCompositeEditPath(taskId, { formIndex = 0, groupIndex = 0, imageId } = {}) {
  const q = new URLSearchParams({
    form: String(formIndex),
    group: String(groupIndex),
  });
  if (imageId) q.set('imageId', String(imageId));
  return `${IPE_WORKBENCH_PATH}/pod-composite-edit/${encodeURIComponent(taskId)}?${q}`;
}

/** 商品合成图专用 IPE 编辑器（单张主图 + 印花/图层参数） */
export function buildGoodsCompositeEditPath(taskId, { formIndex = 0, groupIndex = 0, imageId } = {}) {
  const q = new URLSearchParams({
    form: String(formIndex),
    group: String(groupIndex),
  });
  if (imageId) q.set('imageId', String(imageId));
  return `${IPE_WORKBENCH_PATH}/goods-composite-edit/${encodeURIComponent(taskId)}?${q}`;
}

/** 卖家工作台 Agent 对话页（与 /ipe/agent 运营控制台区分） */
export const IPE_AGENT_CHAT_PATH = `${IPE_WORKBENCH_PATH}/agent-chat`;

/** 功能 IPE豆消耗说明（独立页面） */
export const IPE_CREDIT_CONSUMPTION_PATH = `${IPE_WORKBENCH_PATH}/credit-consumption`;

/** 充值中心（会员 / 服务增强） */
export const IPE_RECHARGE_PATH = `${IPE_WORKBENCH_PATH}/recharge`;

/**
 * @param {{ tab?: 'membership' | 'packs', packScope?: 'personal' | 'team', packGroup?: 'credit' | 'shop' | 'storage', scope?: 'personal' | 'team', group?: 'credit' | 'shop' | 'storage' }} [options]
 */
export function buildRechargeUrl(options = {}) {
  const params = new URLSearchParams();
  const tab = options.tab === 'packs' ? 'packs' : 'membership';
  params.set('tab', tab);
  const scope = options.packScope || options.scope;
  if (scope === 'team' || scope === 'personal') {
    params.set('scope', scope);
  }
  const group = options.packGroup || options.group;
  if (tab === 'packs' && ['credit', 'shop', 'storage'].includes(group)) {
    params.set('group', group);
  }
  const qs = params.toString();
  return qs ? `${IPE_RECHARGE_PATH}?${qs}` : IPE_RECHARGE_PATH;
}

/** 会员权益对比（IPE 会员方案页，默认选中「会员」Tab） */
export const IPE_MEMBERSHIP_COMPARE_PATH = `${IPE_CREDIT_CONSUMPTION_PATH}?tab=membership`;

/** @param {{ tab?: 'membership' | 'credits', scroll?: 'consumption' }} [options] */
export function buildCreditConsumptionUrl(options = {}) {
  const params = new URLSearchParams();
  if (options.tab === 'membership' || options.tab === 'credits') {
    params.set('tab', options.tab);
  }
  if (options.scroll === 'consumption') {
    params.set('scroll', 'consumption');
  }
  const qs = params.toString();
  return qs ? `${IPE_CREDIT_CONSUMPTION_PATH}?${qs}` : IPE_CREDIT_CONSUMPTION_PATH;
}

/** 兼容旧工作台路径 */
export const LEGACY_FUNDING_TOOLS_PATH = '/funding-tools';

export function isIpeLoginPath(pathname) {
  const p = pathname || '';
  return IPE_LOGIN_PATHS.some((path) => p === path || p.startsWith(`${path}?`));
}

export function isIpeWorkbenchPath(pathname) {
  const p = pathname || '';
  return p === IPE_WORKBENCH_PATH
    || p.startsWith(`${IPE_WORKBENCH_PATH}/`)
    || p.startsWith(`${IPE_WORKBENCH_PATH}?`);
}

/** 将 /funding-tools 前缀替换为 /ipe，用于旧链接重定向 */
export function migrateLegacyWorkbenchPath(pathname, search = '', hash = '') {
  const p = pathname || '';
  const target = p.startsWith(LEGACY_FUNDING_TOOLS_PATH)
    ? `${IPE_WORKBENCH_PATH}${p.slice(LEGACY_FUNDING_TOOLS_PATH.length)}`
    : p;
  return `${target}${search || ''}${hash || ''}`;
}

/** 登录后仅允许跳回工作台路径，防止开放重定向 */
export function isSafeWorkbenchRedirect(path) {
  const p = (path || '').trim();
  if (!p.startsWith('/') || p.startsWith('//')) return false;
  const pathOnly = p.split('?')[0].split('#')[0];
  return pathOnly === IPE_WORKBENCH_PATH || pathOnly.startsWith(`${IPE_WORKBENCH_PATH}/`);
}

/** IPE 使用手册（站内教程中心） */
export const IPE_MANUAL_HOME_PATH = '/ipe/manual';
export const IPE_MANUAL_LOGIN_PATH = '/ipe/manual/login';

/** IPE 浏览器采集插件 · 安装引导页 */
export const IPE_PLUGIN_INSTALL_PATH = `${IPE_WORKBENCH_PATH}/plugin/install`;

/** @param {string} slug */
export function buildManualNodePath(slug) {
  return `${IPE_MANUAL_HOME_PATH}/${encodeURIComponent(slug || '')}`;
}

export function isManualPath(pathname) {
  const p = pathname || '';
  return p === IPE_MANUAL_HOME_PATH
    || p.startsWith(`${IPE_MANUAL_HOME_PATH}/`);
}

/** 在新标签页打开链接（不用 window.open 的 features，避免被当成弹窗） */
export function openUrlInNewTab(url) {
  if (!url || typeof window === 'undefined') return;
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function buildLoginUrlWithRedirect(location) {
  const pathname = location?.pathname || '';
  if (isIpeLoginPath(pathname)) return IPE_LOGIN_PATH;
  const target = `${pathname}${location?.search || ''}${location?.hash || ''}`;
  return `${IPE_LOGIN_PATH}?redirect=${encodeURIComponent(target)}`;
}
